/**
 * PhotoLightbox.jsx — Vollbild-Bildbetrachter mit Werkzeugen.
 *
 * Features:
 *   • Vor/Zurück durch die Foto-Liste (←/→ Tasten, Buttons, Klick auf Pfeile)
 *   • Drehen 90° links/rechts (CSS-Transform, optional persistierbar)
 *   • Zoom in/out + Reset (0.5×–4×)
 *   • Download des Originalbildes (Browser-Save-Dialog)
 *   • Löschen (mit Bestätigung)
 *   • AI-Schadenserkennung (sofern noch nicht analysiert)
 *   • Drag-to-Pan beim gezoomten Bild
 *   • ESC-Taste schließt das Modal
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  X, ChevronLeft, ChevronRight, RotateCcw, RotateCw, ZoomIn, ZoomOut,
  Maximize2, Download, Trash2, Sparkles, Loader2,
} from "lucide-react";
import AuthImg from "./AuthImg";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

export default function PhotoLightbox({
  photos,            // Array — vollständige Foto-Liste der Akte (für Vor/Zurück)
  index,             // aktueller Index (kontrolliert vom Parent)
  onIndexChange,     // (newIndex) => void
  onClose,           // () => void
  onDelete,          // (photo) => Promise<void> — Parent kümmert sich um State-Update
  onAnalyze,         // optional: (photoId) => Promise<void>
  analyzing = null,  // ID des aktuell analysierten Fotos (für Spinner)
}) {
  const { accessToken } = useAuth();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);     // in 90°-Schritten
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);

  const total = photos?.length || 0;
  const photo = total > 0 ? photos[index] : null;

  // Reset zoom + rotation when navigating to another photo
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  }, [photo?.id]);

  const goPrev = useCallback(() => {
    if (total < 2) return;
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (total < 2) return;
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  // Keyboard navigation
  useEffect(() => {
    if (!photo) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
      else if (e.key === "-") setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
      else if (e.key === "0") { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); }
      else if (e.key === "r" || e.key === "R") setRotation((r) => (r + 90) % 360);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photo, onClose, goPrev, goNext]);

  if (!photo) return null;

  const handleDownload = () => {
    // Direkt-Download via Auth-Query — gleicher Weg wie ZIP-Download
    const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const url = `${backendBase}/api/files/download/${photo.storage_path}?auth=${encodeURIComponent(accessToken || "")}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = photo.filename || `foto_${photo.id}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Download gestartet");
  };

  const handleDelete = async () => {
    if (!window.confirm(
      `Foto „${photo.filename || photo.id}" wirklich löschen?\n\nDieser Vorgang kann nicht rückgängig gemacht werden.`
    )) return;
    try {
      await onDelete(photo);
      // Wenn das war das letzte Foto → schließen, sonst eins zurückspringen
      if (total <= 1) {
        onClose();
      } else if (index >= total - 1) {
        onIndexChange(total - 2);
      }
      // Wenn nicht das letzte: index bleibt, Parent updated photos[] → nächstes rückt nach
      toast.success("Lichtbild gelöscht");
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen"));
    }
  };

  const handleZoom = (delta) => {
    setZoom((z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z + delta)));
  };

  // Pan only when zoomed
  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const onMouseMove = (e) => {
    if (!dragStart) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onMouseUp = () => setDragStart(null);

  const transformStyle = {
    transform: `translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg) scale(${zoom})`,
    transition: dragStart ? "none" : "transform 0.18s ease-out",
    cursor: zoom > 1 ? (dragStart ? "grabbing" : "grab") : "default",
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex flex-col"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      {/* Top-Toolbar */}
      <div
        className="px-4 py-2.5 bg-black/40 backdrop-blur-md border-b border-white/10 flex items-center gap-2 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs font-semibold tracking-tight" data-testid="lightbox-counter">
          {index + 1} <span className="text-white/50">/ {total}</span>
        </div>
        <div className="text-xs text-white/60 truncate flex-1 min-w-0" title={photo.filename}>
          {photo.filename || `Foto #${photo.id?.slice(0, 6)}`}
        </div>

        <ToolBtn onClick={() => setRotation((r) => (r + 270) % 360)} title="Nach links drehen (R)" testid="lightbox-rotate-left">
          <RotateCcw className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn onClick={() => setRotation((r) => (r + 90) % 360)} title="Nach rechts drehen" testid="lightbox-rotate-right">
          <RotateCw className="h-4 w-4" />
        </ToolBtn>
        <span className="w-px h-5 bg-white/15 mx-0.5" />
        <ToolBtn onClick={() => handleZoom(-ZOOM_STEP)} disabled={zoom <= ZOOM_MIN} title="Verkleinern (−)" testid="lightbox-zoom-out">
          <ZoomOut className="h-4 w-4" />
        </ToolBtn>
        <div className="text-[11px] tabular-nums text-white/70 w-10 text-center" data-testid="lightbox-zoom-value">
          {Math.round(zoom * 100)}%
        </div>
        <ToolBtn onClick={() => handleZoom(ZOOM_STEP)} disabled={zoom >= ZOOM_MAX} title="Vergrößern (+)" testid="lightbox-zoom-in">
          <ZoomIn className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn onClick={() => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); }} title="Zurücksetzen (0)" testid="lightbox-reset">
          <Maximize2 className="h-4 w-4" />
        </ToolBtn>
        <span className="w-px h-5 bg-white/15 mx-0.5" />
        <ToolBtn onClick={handleDownload} title="Original herunterladen" testid="lightbox-download">
          <Download className="h-4 w-4" />
        </ToolBtn>
        {onAnalyze && !photo.ai_damage_description && (
          <ToolBtn
            onClick={() => onAnalyze(photo.id)}
            disabled={analyzing === photo.id}
            title="KI-Schadenserkennung"
            testid="lightbox-analyze"
          >
            {analyzing === photo.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          </ToolBtn>
        )}
        <ToolBtn onClick={handleDelete} title="Foto löschen" testid="lightbox-delete" tone="danger">
          <Trash2 className="h-4 w-4" />
        </ToolBtn>
        <span className="w-px h-5 bg-white/15 mx-0.5" />
        <ToolBtn onClick={onClose} title="Schließen (Esc)" testid="lightbox-close">
          <X className="h-4 w-4" />
        </ToolBtn>
      </div>

      {/* Hauptbild + Nav-Pfeile */}
      <div
        className="flex-1 relative flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {/* Prev */}
        {total > 1 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition disabled:opacity-30"
            data-testid="lightbox-prev"
            title="Vorheriges Foto (←)"
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
        )}

        <div className="select-none" style={transformStyle}>
          <AuthImg
            path={photo.storage_path}
            token={accessToken}
            alt={photo.filename || ""}
            className="max-h-[78vh] max-w-[92vw] object-contain pointer-events-none"
            draggable={false}
          />
        </div>

        {/* Next */}
        {total > 1 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition"
            data-testid="lightbox-next"
            title="Nächstes Foto (→)"
          >
            <ChevronRight className="h-7 w-7" />
          </button>
        )}
      </div>

      {/* Bottom: Analyse-Text */}
      {photo.ai_damage_description && (
        <div
          className="px-5 py-3 bg-black/50 backdrop-blur-md border-t border-white/10 text-white text-xs max-h-32 overflow-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-analysis"
        >
          <span className="text-emerald-300 font-bold">KI-Analyse:</span>{" "}
          <span className="text-white/85 whitespace-pre-wrap">{photo.ai_damage_description}</span>
        </div>
      )}
    </div>
  );
}

function ToolBtn({ children, onClick, disabled, title, testid, tone }) {
  const base = "inline-flex items-center justify-center h-8 w-8 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed";
  const colors = tone === "danger"
    ? "text-rose-300 hover:bg-rose-500/30 hover:text-rose-100"
    : "text-white/85 hover:bg-white/15 hover:text-white";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`${base} ${colors}`}
    >
      {children}
    </button>
  );
}
